import { baseApi } from './baseApi';

export interface TenantPlan {
  key: string;
  name: string;
  tier: 'colaborador' | 'pro' | 'elite' | 'black' | 'amedida';
  priceUsd: number | null;
  custom: boolean;
  popular: boolean;
  limits: { users: number | null; gustavoMonthlyCredits: number | null; properties: number | null };
  features: string[];
  insights: string;
  website: string;
}

export interface TenantSubInfo {
  subscription: { status: string; currentPeriodEnd: string; gustavoCreditsUsedThisPeriod: number; cancelAtPeriodEnd: boolean; domainAddon: boolean; autoRenew: boolean } | null;
  plan: { key: string; name: string; priceUsd: number | null; custom: boolean } | null;
  pendingPlan: { key: string; name: string; priceUsd: number | null } | null;
  plans: TenantPlan[];
  savedCards: Array<{ id: string; brand?: string; last4?: string; isDefault: boolean }>;
  dlocalConfigured: boolean;
}

export interface TenantPayment {
  id: string; date: string; amount: number; currency: string; status: string;
  brand: string | null; last4: string | null;
}

export const tenantSubscriptionApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getTenantSubscription: build.query<TenantSubInfo, void>({
      query: () => '/tenant-subscription/me',
      transformResponse: (r: { data: TenantSubInfo }) => r.data,
      providesTags: ['Subscription'],
    }),
    renewTenantSubscription: build.mutation<{ status: string; redirectUrl?: string }, { savedCardId?: string }>({
      query: (body) => ({ url: '/tenant-subscription/renew', method: 'POST', body }),
      transformResponse: (r: { data: { status: string; redirectUrl?: string } }) => r.data,
      invalidatesTags: ['Subscription'],
    }),
    renewTenantWithNewCard: build.mutation<{ paymentId: string; merchantCheckoutToken: string }, { successUrl?: string; backUrl?: string } | void>({
      query: (body) => ({ url: '/tenant-subscription/renew/new-card', method: 'POST', body: body ?? {} }),
      transformResponse: (r: { data: { paymentId: string; merchantCheckoutToken: string } }) => r.data,
    }),
    getTenantPayments: build.query<TenantPayment[], void>({
      query: () => '/tenant-subscription/payments',
      transformResponse: (r: { data: TenantPayment[] }) => r.data,
      providesTags: ['Subscription'],
    }),
    cancelTenantSubscription: build.mutation<{ cancelAtPeriodEnd: boolean; until?: string }, void>({
      query: () => ({ url: '/tenant-subscription/cancel', method: 'POST' }),
      transformResponse: (r: { data: { cancelAtPeriodEnd: boolean; until?: string } }) => r.data,
      invalidatesTags: ['Subscription'],
    }),
    reactivateTenantSubscription: build.mutation<{ cancelAtPeriodEnd: boolean }, void>({
      query: () => ({ url: '/tenant-subscription/reactivate', method: 'POST' }),
      transformResponse: (r: { data: { cancelAtPeriodEnd: boolean } }) => r.data,
      invalidatesTags: ['Subscription'],
    }),
    changeTenantPlan: build.mutation<
      { immediate: boolean; amount?: number; status?: string; redirectUrl?: string | null; pendingPlan?: string | null; appliesAt?: string },
      { planKey: string; domainAddon?: boolean }
    >({
      query: (body) => ({ url: '/tenant-subscription/change-plan', method: 'POST', body }),
      transformResponse: (r: { data: { immediate: boolean; amount?: number; status?: string; redirectUrl?: string | null; pendingPlan?: string | null; appliesAt?: string } }) => r.data,
      invalidatesTags: ['Subscription'],
    }),
  }),
});

export const {
  useGetTenantSubscriptionQuery,
  useLazyGetTenantSubscriptionQuery,
  useRenewTenantSubscriptionMutation,
  useRenewTenantWithNewCardMutation,
  useGetTenantPaymentsQuery,
  useCancelTenantSubscriptionMutation,
  useReactivateTenantSubscriptionMutation,
  useChangeTenantPlanMutation,
} = tenantSubscriptionApi;
